<template>
  <div class="case-sounds container">
    <div class="backgrounds-wrapper-container">
      <div class="backgrounds-wrapper">
        <img class="background" :src="background">
        <img v-if="overlay" class="background" :src="overlay">
        <div v-if="overlays">
          <img v-for="(overlayItem, index) in overlays" :key="index" class="background" :src="overlayItem">
        </div>
        <span
          v-for="spot in spots"
          :key="'button-' + spot.category + ' - ' + spot.position"
          class="spot spot-position"
          :class="{active: state.activeSpotPosition === spot.position}"
          :style="{left: spot.x, top: spot.y}"
        >
          {{ spot.label || spot.position + 1 }}
        </span>
      </div>
    </div>
    <div class="sounds-panel" data-testid="sounds-panel-container">
      <div
        v-for="spot in spots"
        :key="'select-' + spot.category + ' - ' + spot.position"
        class="spot-sound-select-container"
        :class="{spacer: spot.spacer}"
      >
        <sound-select
          :sounds="soundsByCategory[spot.category || category]"
          :value="getSpotValue(spot)"
          :volume="getSpotVolume(spot)"
          :label="`${spot.label || spot.position + 1}`"
          :disabled="readonly"
          @input="value => setSpotValue(spot, value)"
          @changeVolume="volume => setSpotVolume(spot, volume)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import groupBy from 'lodash/groupBy';
import mapValues from 'lodash/mapValues';
import $ from 'jquery';

import SoundSelect from './SoundSelect';

export default {
  name: 'case-sounds',
  components: {
    SoundSelect,
  },
  props: {
    caseConf: {
      type: Object,
      default: () => ({
        language: 'en',
      }),
    },
    spots: {
      type: Array,
      default: () => []
    },
    sounds: {
      type: Array,
      default: () => []
    },
    category: {
      type: String,
      default: null
    },
    orientation: {
      type: String,
      default: null
    },
    background: {
      type: String,
      default: null
    },
    overlay: {
      type: String,
      default: null
    },
    overlays: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      state: {
        activeSpotPosition: null,
      },
      previewSound: null,
    };
  },
  computed: {
    categorySounds() {
      return this.sounds.filter(s => s.category === this.category);
    },
    soundsByCategoryAndResourceName() {
      return mapValues(this.soundsByCategory, sounds => keyBy(sounds, 'resourceName'));
    },
    soundsByCategory() {
      return groupBy(this.sounds, 'category');
    },
    normalSoundByCategory() {
      return mapValues(this.soundsByCategoryAndResourceName, sounds => sounds.NORMAL);
    },
  },
  created() {
    $(window).on('click', this.unfocusSpot);
  },
  beforeDestroy() {
    $(window).off('click', this.unfocusSpot);
  },
  methods: {
    focusSpot(position, event) {
      if (position === null) {
        this.stopPreview();
      }
      event.stopPropagation();
      this.state.activeSpotPosition = position;
    },
    unfocusSpot() {
      this.state.activeSpotPosition = null;
    },
    toggleSpot(position, event) {
      event.stopPropagation();
      const { activeSpotPosition } = this.state;
      if (position === activeSpotPosition) {
        this.state.activeSpotPosition = null;
      } else {
        this.state.activeSpotPosition = position;
      }
    },
    getSpotClass(spot) {
      const category = spot.category || this.category;
      return this.getSpotValue(spot) === this.normalSoundByCategory[category].id
        ? 'color'
        : 'md-primary bg';
    },
    getSpotValue(spot, caseConf = this.caseConf) {
      const { position } = spot;
      const category = spot.category || this.category;
      const orientation = spot.orientation || this.orientation;
      const defaultSound = this.normalSoundByCategory[category];
      // eslint-disable-next-line no-underscore-dangle
      if (!caseConf || !caseConf._sounds) {
        return defaultSound.id;
      }
      return get(
        // eslint-disable-next-line no-underscore-dangle
        caseConf._sounds.find(
          s =>
            s.options.position === position &&
            s.options.type === category &&
            (!orientation || s.options.orientation === orientation),
        ),
        'id',
        defaultSound.id,
      );
    },
    getSpotVolume(spot, caseConf = this.caseConf) {
      const { position } = spot;
      const category = spot.category || this.category;
      const orientation = spot.orientation || this.orientation;
      if (!caseConf || !caseConf._sounds) {
        return 1;
      }
      return get(
        caseConf._sounds.find(
          s =>
            s.options.position === position &&
            s.options.type === category &&
            (!orientation || s.options.orientation === orientation),
        ),
        'options.volume',
        1,
      );
    },
    setSpotValue(spot, value) {
      const { position } = spot;
      const category = spot.category || this.category;
      const orientation = spot.orientation || this.orientation;
      const _sounds = this.caseConf._sounds
        ? this.caseConf._sounds.filter(
            s =>
              // heart sounds don't have orientation so we should not compare them
              (orientation &&
                s.options.orientation !== orientation) ||
              s.options.type !== category ||
              s.options.position !== position,
          )
        : [];
      _sounds.push({
        id: value,
        options: {
          orientation,
          position,
          type: category,
          volume: this.getSpotVolume(position),
        },
      });
      this.emitUpdate(_sounds);
    },
    setSpotVolume(spot, volume) {
      const { position } = spot;
      const category = spot.category || this.category;
      const orientation = spot.orientation || this.orientation;
      const caseSound = this.caseConf._sounds.find(
        s =>
          // heart sounds don't have orientation so we should not compare them
          orientation &&
          s.options.orientation === orientation &&
          s.options.type === category &&
          s.options.position === position,
      );
      if (!caseSound) {
        this.setSpotValue(spot, this.normalSoundByCategory[category].id);
        this.setSpotVolume(spot, volume);
        return;
      }
      caseSound.options.volume = volume;
      this.emitUpdate(this.caseConf._sounds);
    },
    getSpotSoundName(spot) {
      const soundId = this.getSpotValue(spot);
      if (!soundId || soundId === 'default') {
        return 'Normal';
      }
      return this.sounds.find(s => s.id === soundId).resourceName;
    },
    emitUpdate(_sounds) {
      this.$emit('update', {
        // eslint-disable-next-line no-underscore-dangle
        _sounds,
      });
    },
  },
};
</script>

<style scoped>
.container {
  width: 100%;
  display: flex;
  justify-content: center;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
}

.backgrounds-wrapper-container {
  width: 40%;
  display: flex;
  align-items: center;
}

.backgrounds-wrapper {
  width: 100%;
  /* Keep the aspect ratio of the image, padding-bottom = image ratio (height / width) */
  padding-bottom: 76%;
}

.background {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
}

.sounds-panel {
  width: 60%;
  padding: 0 10px;
}

.backgrounds-wrapper {
  position: relative;
  display: inline-block;
}

.spot {
  position: absolute;
  /* Change the pivot point of the spot to its center */
  transform: translateX(-50%) translateY(-50%);
  z-index: 5;
}

.spot-position {
  font-size: 20px;
  font-weight: bold;
  text-shadow: 0px 0px 6px rgba(0,0,0,0.5);
}

.spot-button.inactive {
  background-color: white !important;
}

.spot.active .spot-button {
  background-color: grey !important;
}

.spot-sound-select-container.spacer {
  margin-top: 30px;
}
</style>
